/** Demo backups: freeze the current state and restore one, with server-reported progress. */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import EmptyState from '../../components/EmptyState';
import Spinner from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import {
  ApiError,
  errorMessage,
  fetchCurrentDemoJob,
  fetchDemoBackups,
  fetchDemoJob,
  startDemoBackup,
  startDemoRestore,
} from '../../lib/api';
import { fmtDate } from '../../lib/format';
import type { DemoBackup, DemoJob, DemoRestoreResult } from '../../lib/types';
import './demo-backups.css';

interface DemoBackupsCardProps {
  /** Permission / deployment gate; the confirmation is managed by this card. */
  disabledReason: string | null;
}

const LABEL_MAX = 60;

function elapsedLabel(job: DemoJob, now: number): string {
  const start = Date.parse(job.started_at);
  const end = job.finished_at === null ? now : Date.parse(job.finished_at);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return '—';
  const seconds = Math.max(0, Math.floor((end - start) / 1000));
  return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
}

function sizeLabel(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
}

function backupName(backup: Pick<DemoBackup, 'label' | 'created_at'>): string {
  return backup.label !== '' ? `„${backup.label}“` : `vom ${fmtDate(backup.created_at)}`;
}

function completionMessage(job: DemoJob): string {
  if (job.kind === 'backup') {
    const backup = job.result as DemoBackup | null;
    return backup !== null
      ? `Backup ${backupName(backup)} erstellt (${sizeLabel(backup.size_bytes)}).`
      : 'Backup erstellt.';
  }
  const result = job.result as DemoRestoreResult | null;
  return result !== null
    ? `Demo-Stand ${backupName(result.backup)} in ${Math.round(result.duration_seconds)} s wiederhergestellt.`
    : 'Demo-Stand wiederhergestellt.';
}

export default function DemoBackupsCard({ disabledReason }: DemoBackupsCardProps): ReactNode {
  const toast = useToast();
  const [backups, setBackups] = useState<DemoBackup[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [job, setJob] = useState<DemoJob | null>(null);
  const [checking, setChecking] = useState(true);
  const [starting, setStarting] = useState<DemoJob['kind'] | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [now, setNow] = useState(Date.now);
  const jobRef = useRef<DemoJob | null>(null);
  const startInFlight = useRef(false);
  const pendingStart = useRef<{ previousJobId: string | null } | null>(null);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const loadBackups = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const next = (await fetchDemoBackups(signal)).backups;
      if (!mounted.current) return;
      setBackups(next);
      setListError(null);
      // Keep the selection only while it still exists; default to the newest.
      setSelected((current) => (next.some((backup) => backup.id === current) ? current : next[0]?.id ?? null));
    } catch (error) {
      if (mounted.current && !signal?.aborted) setListError(errorMessage(error));
    }
  }, []);

  useEffect(() => {
    if (disabledReason !== null) return;
    const controller = new AbortController();
    void loadBackups(controller.signal);
    return () => controller.abort();
  }, [disabledReason, loadBackups]);

  const receiveJob = useCallback((next: DemoJob | null): void => {
    const previous = jobRef.current;
    const newJobAfterStart = pendingStart.current !== null
      && next !== null && next.id !== pendingStart.current.previousJobId;
    // Reconcile a lost POST response even if the job finishes before GET returns.
    // Clear the marker after reconciliation so later historical jobs stay quiet.
    if (newJobAfterStart || !startInFlight.current) pendingStart.current = null;
    jobRef.current = next;
    setJob(next);
    if (next?.status === 'running' || newJobAfterStart) {
      setConfirmed(false);
      setActionError(null);
    }
    // Historical results remain visible after reload without repeating a toast.
    const observedCompletion = previous?.id === next?.id && previous?.status === 'running';
    if (next === null || next.status === 'running' || (!observedCompletion && !newJobAfterStart)) return;
    if (next.status === 'completed') {
      toast.success(completionMessage(next));
      if (next.kind === 'backup') setLabel('');
    } else {
      toast.error(next.error ?? 'Demo-Operation fehlgeschlagen.');
    }
    void loadBackups();
  }, [loadBackups, toast]);

  useEffect(() => {
    if (disabledReason !== null || starting !== null) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let request: AbortController | undefined;

    async function poll(): Promise<void> {
      const controller = new AbortController();
      request = controller;
      const timeout = setTimeout(() => controller.abort(), 15_000);
      let retry = false;
      try {
        const known = jobRef.current;
        let next: DemoJob | null;
        if (known?.status === 'running') {
          try {
            next = await fetchDemoJob(known.id, controller.signal);
          } catch (error) {
            if (!(error instanceof ApiError) || error.status !== 404) throw error;
            // A backend restart may invalidate the remembered job identifier.
            next = (await fetchCurrentDemoJob(controller.signal)).job;
            if (next === null && !stopped) {
              setActionError('Die letzte Demo-Operation ist nicht mehr verfügbar. Bitte den Datenbestand prüfen.');
            }
          }
        } else {
          next = (await fetchCurrentDemoJob(controller.signal)).job;
        }
        if (stopped) return;
        receiveJob(next);
        setPollError(null);
        setChecking(false);
      } catch (error) {
        if (stopped) return;
        retry = true;
        setPollError(controller.signal.aborted
          ? 'Die Statusabfrage hat zu lange gedauert.'
          : errorMessage(error));
      } finally {
        clearTimeout(timeout);
        if (!stopped) {
          // Never overlap requests; a temporary outage does not release the lock.
          timer = setTimeout(() => void poll(), retry ? 3000 : jobRef.current?.status === 'running' ? 1000 : 5000);
        }
      }
    }

    void poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
      request?.abort();
    };
  }, [disabledReason, receiveJob, refresh, starting]);

  const running = job?.status === 'running';
  useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);

  const blocked = disabledReason !== null || checking || starting !== null || running || pollError !== null;
  const busyReason = disabledReason ?? (running
    ? 'Eine Demo-Operation läuft bereits.'
    : checking || pollError !== null
      ? 'Der aktuelle Demo-Status muss zuerst bestätigt werden.'
      : undefined);
  const labelTooLong = label.trim().length > LABEL_MAX;
  const chosen = backups?.find((backup) => backup.id === selected) ?? null;
  const restoreReason = busyReason ?? (chosen === null
    ? 'Bitte zuerst ein Backup auswählen.'
    : !confirmed
      ? 'Bitte zuerst bestätigen, dass der aktuelle Demo-Stand verworfen werden darf.'
      : undefined);

  async function start(kind: DemoJob['kind']): Promise<void> {
    if (blocked || startInFlight.current) return;
    if (kind === 'restore' && (chosen === null || !confirmed)) return;
    if (kind === 'backup' && labelTooLong) return;
    startInFlight.current = true;
    pendingStart.current = { previousJobId: jobRef.current?.id ?? null };
    setStarting(kind);
    setActionError(null);
    try {
      const signal = AbortSignal.timeout(15_000);
      const next = kind === 'backup'
        ? await startDemoBackup(label.trim(), signal)
        : await startDemoRestore(chosen!.id, signal);
      if (mounted.current) receiveJob(next);
    } catch (error) {
      if (mounted.current) {
        setActionError(error instanceof DOMException && error.name === 'TimeoutError'
          ? 'Die Antwort auf den Start dauert zu lange. Der aktuelle Demo-Status wird erneut geprüft.'
          : errorMessage(error));
        // The server may have accepted the request even if its response was lost.
        // Reconcile via GET before allowing another explicit start attempt.
      }
    } finally {
      startInFlight.current = false;
      if (mounted.current) {
        setConfirmed(false);
        setChecking(true);
        setStarting(null);
      }
    }
  }

  const progress = Math.round(Math.max(0, Math.min(100, job?.progress ?? 0)));
  const jobTitle = job === null
    ? ''
    : job.kind === 'backup'
      ? `Backup${job.label ? ` „${job.label}“` : ''}`
      : `Wiederherstellung${job.label ? ` „${job.label}“` : ''}`;

  return (
    <>
      <div className="ops-grid">
        <section className="ops-card">
          <h3>Backup erstellen</h3>
          <div className="ops-desc">
            Friert den aktuellen Demo-Stand ein: die komplette CMDBuild-Datenbank sowie Asset-Speicher und
            SBOM-Dateien. Benutzer, Dependency-Track und CSAF-Quellen gehören nicht dazu.
          </div>
          <div className="field">
            <label htmlFor="demo-backup-label">Bezeichnung (optional)</label>
            <input
              id="demo-backup-label"
              type="text"
              value={label}
              maxLength={LABEL_MAX + 20}
              placeholder="z. B. vor Import v2.0"
              aria-invalid={labelTooLong}
              aria-describedby="demo-backup-label-hint"
              disabled={blocked}
              onChange={(event) => setLabel(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void start('backup');
              }}
            />
            <span id="demo-backup-label-hint" className={labelTooLong ? 'err' : 'hint'}>
              {labelTooLong ? `Höchstens ${LABEL_MAX} Zeichen.` : 'Erscheint in der Liste der Backups.'}
            </span>
          </div>
          <div className="ops-actions">
            <button
              type="button"
              className="primary"
              onClick={() => void start('backup')}
              disabled={blocked || labelTooLong}
              title={busyReason}
            >
              {starting === 'backup' ? <Spinner variant="on-accent" label="Backup wird gestartet…" /> : 'Backup erstellen'}
            </button>
          </div>
        </section>

        <section className="ops-card ops-destructive">
          <h3>Backup wiederherstellen</h3>
          <div className="ops-desc">
            Ersetzt den aktuellen Demo-Stand vollständig durch das gewählte Backup. CMDBuild ist dabei etwa eine
            Minute nicht erreichbar. Sie bleiben angemeldet.
          </div>
          {backups === null && listError === null ? <Spinner label="Backups werden geladen…" /> : null}
          {listError !== null ? (
            <Banner kind="error">
              Backups konnten nicht geladen werden: {listError}
              <button type="button" className="demo-job-retry" onClick={() => void loadBackups()}>Erneut laden</button>
            </Banner>
          ) : null}
          {backups !== null && backups.length === 0 ? (
            <EmptyState compact title="Noch kein Backup vorhanden">
              Erstellen Sie zuerst ein Backup unter „Backup erstellen“.
            </EmptyState>
          ) : null}
          {backups !== null && backups.length > 0 ? (
            <fieldset className="demo-backup-list" disabled={blocked}>
              <legend>Backup auswählen</legend>
              {backups.map((backup) => (
                <label key={backup.id} className={`demo-backup${backup.id === selected ? ' on' : ''}`}>
                  <input
                    type="radio"
                    name="demo-backup"
                    value={backup.id}
                    checked={backup.id === selected}
                    onChange={() => {
                      setSelected(backup.id);
                      setConfirmed(false);
                    }}
                  />
                  <span className="demo-backup-text">
                    <strong>{backup.label !== '' ? backup.label : 'Ohne Bezeichnung'}</strong>
                    <span className="muted">
                      {fmtDate(backup.created_at)} · {sizeLabel(backup.size_bytes)} · {backup.assets} Assets ·{' '}
                      {backup.sboms} SBOMs
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>
          ) : null}
          <label className="check">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
              disabled={blocked || chosen === null}
            />
            Ja, den aktuellen Demo-Stand verwerfen{chosen !== null ? ` und ${backupName(chosen)} laden` : ''}
          </label>
          <div className="ops-actions">
            <button
              type="button"
              className="danger"
              onClick={() => void start('restore')}
              disabled={blocked || chosen === null || !confirmed}
              title={restoreReason}
            >
              {starting === 'restore'
                ? <Spinner variant="on-accent" label="Wiederherstellung wird gestartet…" />
                : 'Backup wiederherstellen'}
            </button>
          </div>
        </section>
      </div>

      {job !== null ? (
        <div className="demo-job-progress" data-status={job.status}>
          <div className="demo-job-progress-heading">
            <strong aria-live="polite" aria-atomic="true">{jobTitle}: {job.message}</strong>
            <span>{progress} %</span>
          </div>
          <div
            className="demo-job-progress-track"
            role="progressbar"
            aria-label={`Fortschritt: ${jobTitle}`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
            aria-valuetext={`${progress} %: ${job.message}`}
          >
            <span style={{ width: `${progress}%` }} />
          </div>
          <div className="demo-job-progress-footer">
            <span>Laufzeit: {elapsedLabel(job, now)}</span>
            {running ? <span>Sie können die Seite verlassen. Der Vorgang läuft weiter.</span> : null}
          </div>
        </div>
      ) : null}

      {pollError !== null ? (
        <Banner kind="warn">
          Fortschritt derzeit nicht abrufbar: {pollError} Die Statusabfrage wird automatisch wiederholt.
          <button type="button" className="demo-job-retry" onClick={() => setRefresh((value) => value + 1)}>Status erneut abrufen</button>
        </Banner>
      ) : null}
      {actionError !== null ? <Banner kind="error">{actionError}</Banner> : null}
      {job?.status === 'failed' ? <Banner kind="error">{job.error ?? 'Demo-Operation fehlgeschlagen.'}</Banner> : null}
    </>
  );
}
